import * as z from 'zod';

export const RemoveMemberRequest = z.object({
  userId: z.string().describe('User ID of the team member to remove.'),
});
export const RemoveMemberResponse = z.object({});
export const SearchTeamRequest = z.object({
  options: z
    .object({
      query: z
        .string()
        .describe(
          'Free text to match in searchable fields.\n\nFor example, search for specific values in\n`name` or `email`.'
        )
        .optional()
        .nullable(),
      orderBy: z
        .array(
          z.object({
            fieldName: z.enum(['Name', 'JoinedAt']).optional(),
            direction: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object for narrowing search results.\n\nFor example, to return only users with a specific role: `{"filter": {"roleId": "6600344420111308827"}}`\n\nSee [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language)\nfor more information.\n\nSupported values: `inviteType` and `roleId`.'
        )
        .optional()
        .nullable(),
      facets: z.array(z.enum(['Roles', 'InviteStatus', 'Users'])).optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Paging options.')
        .optional(),
    })
    .describe('Filter options.')
    .optional(),
});
export const SearchTeamResponse = z.object({
  facets: z
    .array(
      z.object({
        facetType: z
          .enum(['Roles', 'InviteStatus', 'Users'])
          .describe('Type of facet.')
          .optional(),
        values: z
          .array(
            z.object({
              value: z
                .string()
                .describe('Supported values: `Roles`, `InviteStatus`, `Users`.')
                .optional(),
              count: z
                .number()
                .int()
                .describe('Number of existing items for the `value`.')
                .optional(),
            })
          )
          .optional(),
      })
    )
    .optional(),
  teamMembers: z
    .array(
      z.intersection(
        z.object({}),
        z.xor([
          z.object({
            user: z.never().optional(),
            invite: z.never().optional(),
          }),
          z.object({
            invite: z.never().optional(),
            user: z
              .object({
                _id: z.string().describe('User ID.').optional(),
                email: z
                  .string()
                  .describe("User's email address.")
                  .email()
                  .optional()
                  .nullable(),
                name: z
                  .object({
                    firstName: z
                      .string()
                      .describe("User's first name.")
                      .optional(),
                    lastName: z
                      .string()
                      .describe("User's last name.")
                      .optional(),
                  })
                  .describe("User's name, when provided.")
                  .optional(),
                profileImage: z
                  .string()
                  .describe("URL to user's profile image, when provided.")
                  .optional()
                  .nullable(),
                joinedTeamAt: z
                  .date()
                  .describe('Date the user joined the team.')
                  .optional()
                  .nullable(),
                assignments: z
                  .array(
                    z.object({
                      policyId: z
                        .string()
                        .describe(
                          'Role assigned to the user. To retrieve all available roles, call Get Roles Info.'
                        )
                        .optional()
                        .nullable(),
                      assignmentId: z
                        .string()
                        .describe('Unique ID for this specific assignment.')
                        .optional()
                        .nullable(),
                      restrictions: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              resource: z.never().optional(),
                              conditions: z.never().optional(),
                              site: z.never().optional(),
                              locations: z.never().optional(),
                            }),
                            z.object({
                              conditions: z.never().optional(),
                              site: z.never().optional(),
                              locations: z.never().optional(),
                              resource: z
                                .object({
                                  resourceType: z
                                    .enum(['UNKNOWN_RESOURCE_TYPE', 'SITE'])
                                    .describe('Resource type.')
                                    .optional(),
                                  _id: z
                                    .string()
                                    .describe('Resource ID.')
                                    .optional(),
                                  value: z.string().optional().nullable(),
                                })
                                .describe('Deprecated.'),
                            }),
                            z.object({
                              resource: z.never().optional(),
                              site: z.never().optional(),
                              locations: z.never().optional(),
                              conditions: z
                                .object({
                                  conditions: z
                                    .array(
                                      z.object({
                                        conditionType: z
                                          .enum([
                                            'UNKNOWN_CONDITION_TYPE',
                                            'FOLDER',
                                          ])
                                          .describe('Condition type.')
                                          .optional(),
                                        _id: z
                                          .string()
                                          .describe('Condition ID.')
                                          .optional(),
                                        value: z
                                          .string()
                                          .describe(
                                            'Expected value of the condition. When `conditionType` = "FOLDER", this is the folder path.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "List of conditions restricting the user's access. Currently only folder conditions are supported."
                                ),
                            }),
                            z.object({
                              resource: z.never().optional(),
                              conditions: z.never().optional(),
                              locations: z.never().optional(),
                              site: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Site ID.')
                                    .optional(),
                                  value: z
                                    .string()
                                    .describe('Site name.')
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Site where the assignment restrictions apply.'
                                ),
                            }),
                            z.object({
                              resource: z.never().optional(),
                              conditions: z.never().optional(),
                              site: z.never().optional(),
                              locations: z
                                .object({
                                  ids: z.array(z.string()).max(20).optional(),
                                })
                                .describe(
                                  'Business locations where the assignment restrictions apply. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'The asset where a user is assigned access in an assignment. When empty, the role covers all assets, with no restrictions to specific sites or folders.'
                        )
                        .optional(),
                      subject: z
                        .object({
                          _id: z
                            .string()
                            .describe('Identity ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          subjectType: z
                            .enum([
                              'UNKNOWN',
                              'ACCOUNT',
                              'USER',
                              'USER_GROUP',
                              'MEMBER_GROUP',
                              'VISITOR_GROUP',
                              'EXTERNAL_APP',
                              'ACCOUNT_GROUP',
                              'WIX_APP',
                            ])
                            .describe('Identity type.')
                            .optional(),
                        })
                        .describe(
                          'Identity assigned to the asset in an assignment, referred to as subject. Supported subjects include user IDs, account IDs, and app IDs.'
                        )
                        .optional(),
                    })
                  )
                  .optional(),
              })
              .describe('Existing team member data.'),
          }),
          z.object({
            user: z.never().optional(),
            invite: z
              .object({
                _id: z
                  .string()
                  .describe('Invite ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                email: z
                  .string()
                  .describe("Invitee's email address.")
                  .email()
                  .optional()
                  .nullable(),
                status: z
                  .enum(['Pending', 'Used', 'Deleted', 'Declined', 'Expired'])
                  .describe('Invite status.')
                  .optional(),
                acceptLink: z
                  .string()
                  .describe('URL of direct link to accept the invite.')
                  .optional()
                  .nullable(),
                dateCreated: z
                  .date()
                  .describe('Date the invite was created.')
                  .optional()
                  .nullable(),
                dateUpdated: z
                  .date()
                  .describe('Date the invite was last updated.')
                  .optional()
                  .nullable(),
                assignments: z
                  .array(
                    z.object({
                      policyId: z
                        .string()
                        .describe(
                          'Role ID that will be assigned once the invite is accepted.'
                        )
                        .optional()
                        .nullable(),
                      restrictions: z
                        .intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              resource: z.never().optional(),
                              conditions: z.never().optional(),
                              site: z.never().optional(),
                              locations: z.never().optional(),
                            }),
                            z.object({
                              conditions: z.never().optional(),
                              site: z.never().optional(),
                              locations: z.never().optional(),
                              resource: z
                                .object({
                                  resourceType: z
                                    .enum(['UNKNOWN_RESOURCE_TYPE', 'SITE'])
                                    .describe('Resource type.')
                                    .optional(),
                                  _id: z
                                    .string()
                                    .describe('Resource ID.')
                                    .optional(),
                                  value: z.string().optional().nullable(),
                                })
                                .describe('Deprecated.'),
                            }),
                            z.object({
                              resource: z.never().optional(),
                              site: z.never().optional(),
                              locations: z.never().optional(),
                              conditions: z
                                .object({
                                  conditions: z
                                    .array(
                                      z.object({
                                        conditionType: z
                                          .enum([
                                            'UNKNOWN_CONDITION_TYPE',
                                            'FOLDER',
                                          ])
                                          .describe('Condition type.')
                                          .optional(),
                                        _id: z
                                          .string()
                                          .describe('Condition ID.')
                                          .optional(),
                                        value: z
                                          .string()
                                          .describe(
                                            'Expected value of the condition. When `conditionType` = "FOLDER", this is the folder path.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "List of conditions restricting the user's access. Currently only folder conditions are supported."
                                ),
                            }),
                            z.object({
                              resource: z.never().optional(),
                              conditions: z.never().optional(),
                              locations: z.never().optional(),
                              site: z
                                .object({
                                  _id: z
                                    .string()
                                    .describe('Site ID.')
                                    .optional(),
                                  value: z
                                    .string()
                                    .describe('Site name.')
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Site where the assignment restrictions apply.'
                                ),
                            }),
                            z.object({
                              resource: z.never().optional(),
                              conditions: z.never().optional(),
                              site: z.never().optional(),
                              locations: z
                                .object({
                                  ids: z.array(z.string()).max(20).optional(),
                                })
                                .describe(
                                  'Business locations where the assignment restrictions apply. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Assets where the user will be assigned access. When empty, the role covers all assets, with no restrictions to specific sites or folders.'
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                expirationDate: z
                  .date()
                  .describe('Invite expiration date.')
                  .optional()
                  .nullable(),
              })
              .describe('Invited team member data.'),
          }),
        ])
      )
    )
    .optional(),
});
export const GetRolesInfoRequest = z.object({
  options: z
    .object({
      locale: z
        .string()
        .describe(
          'Language of predefined roles names and descriptions to return, in ISO 639 format. Default: `en`.'
        )
        .optional()
        .nullable(),
      filter: z
        .object({
          roleLevel: z.enum(['ALL', 'SITE_LEVEL', 'ACCOUNT_LEVEL']).optional(),
          editorTypes: z
            .array(z.enum(['UNINITIALIZED', 'EDITORX', 'BLOCKS', 'STUDIO']))
            .optional(),
        })
        .describe('Roles to return.')
        .optional(),
    })
    .describe('Filter options.')
    .optional(),
});
export const GetRolesInfoResponse = z.object({
  predefinedRoles: z
    .array(
      z.object({
        _id: z.string().describe('Role ID.').optional(),
        title: z
          .string()
          .describe(
            'Role title, translated according to the request locale. If translation fails, the original title is returned.'
          )
          .optional(),
        description: z
          .string()
          .describe(
            'Role description, translated according to the request locale. If translation fails, the original description is returned.'
          )
          .optional(),
        restrictFromLevel: z
          .enum(['NoRestriction', 'Site', 'Account'])
          .describe(
            'Whether this role is restricted from accessing a specific resource type. Default: `NoRestriction`.'
          )
          .optional(),
      })
    )
    .optional(),
  customRoles: z
    .array(
      z.object({
        _id: z.string().describe('Role ID.').optional(),
        title: z
          .string()
          .describe(
            'Role title, translated according to the request locale. If translation fails, the original title is returned.'
          )
          .optional(),
        description: z
          .string()
          .describe(
            'Role description, translated according to the request locale. If translation fails, the original description is returned.'
          )
          .optional(),
        restrictFromLevel: z
          .enum(['NoRestriction', 'Site', 'Account'])
          .describe(
            'Whether this role is restricted from accessing a specific resource type. Default: `NoRestriction`.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const UpdateTeamMemberAssignmentsRequest = z.object({
  userId: z
    .string()
    .describe('ID of team member being affected.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      newAssignments: z
        .array(
          z.object({
            policyId: z
              .string()
              .describe(
                'Role assigned to the user. To retrieve all available roles, call Get Roles Info.'
              )
              .optional()
              .nullable(),
            assignmentId: z
              .string()
              .describe('Unique ID for this specific assignment.')
              .optional()
              .nullable(),
            restrictions: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    resource: z.never().optional(),
                    conditions: z.never().optional(),
                    site: z.never().optional(),
                    locations: z.never().optional(),
                  }),
                  z.object({
                    conditions: z.never().optional(),
                    site: z.never().optional(),
                    locations: z.never().optional(),
                    resource: z
                      .object({
                        resourceType: z
                          .enum(['UNKNOWN_RESOURCE_TYPE', 'SITE'])
                          .describe('Resource type.')
                          .optional(),
                        _id: z.string().describe('Resource ID.').optional(),
                        value: z.string().optional().nullable(),
                      })
                      .describe('Deprecated.'),
                  }),
                  z.object({
                    resource: z.never().optional(),
                    site: z.never().optional(),
                    locations: z.never().optional(),
                    conditions: z
                      .object({
                        conditions: z
                          .array(
                            z.object({
                              conditionType: z
                                .enum(['UNKNOWN_CONDITION_TYPE', 'FOLDER'])
                                .optional(),
                              _id: z
                                .string()
                                .describe('Condition ID.')
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  'Expected value of the condition. When `conditionType` = "FOLDER", this is the folder path.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        "List of conditions restricting the user's access. Currently only folder conditions are supported."
                      ),
                  }),
                  z.object({
                    resource: z.never().optional(),
                    conditions: z.never().optional(),
                    locations: z.never().optional(),
                    site: z
                      .object({
                        _id: z.string().describe('Site ID.').optional(),
                        value: z
                          .string()
                          .describe('Site name.')
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Site where the assignment restrictions apply.'
                      ),
                  }),
                  z.object({
                    resource: z.never().optional(),
                    conditions: z.never().optional(),
                    site: z.never().optional(),
                    locations: z
                      .object({ ids: z.array(z.string()).max(20).optional() })
                      .describe(
                        'Business locations where the assignment restrictions apply. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).'
                      ),
                  }),
                ])
              )
              .describe(
                'The asset where a user is assigned access in an assignment. When empty, the role covers all assets, with no restrictions to specific sites or folders.'
              )
              .optional(),
            subject: z
              .object({
                _id: z
                  .string()
                  .describe('Identity ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                subjectType: z
                  .enum([
                    'UNKNOWN',
                    'ACCOUNT',
                    'USER',
                    'USER_GROUP',
                    'MEMBER_GROUP',
                    'VISITOR_GROUP',
                    'EXTERNAL_APP',
                    'ACCOUNT_GROUP',
                    'WIX_APP',
                  ])
                  .optional(),
              })
              .describe(
                'Identity assigned to the asset in an assignment, referred to as subject. Supported subjects include user IDs, account IDs, and app IDs.'
              )
              .optional(),
          })
        )
        .max(50),
      assignmentIdsToRemove: z.array(z.string()).min(0).max(50).optional(),
    })
    .describe(
      'Filter options. The `assignmentIdsToRemove` and `newAssignments` fields **must** be passed.'
    ),
});
export const UpdateTeamMemberAssignmentsResponse = z.object({
  assignments: z
    .array(
      z.object({
        policyId: z
          .string()
          .describe(
            'Role assigned to the user. To retrieve all available roles, call Get Roles Info.'
          )
          .optional()
          .nullable(),
        assignmentId: z
          .string()
          .describe('Unique ID for this specific assignment.')
          .optional()
          .nullable(),
        restrictions: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                resource: z.never().optional(),
                conditions: z.never().optional(),
                site: z.never().optional(),
                locations: z.never().optional(),
              }),
              z.object({
                conditions: z.never().optional(),
                site: z.never().optional(),
                locations: z.never().optional(),
                resource: z
                  .object({
                    resourceType: z
                      .enum(['UNKNOWN_RESOURCE_TYPE', 'SITE'])
                      .describe('Resource type.')
                      .optional(),
                    _id: z.string().describe('Resource ID.').optional(),
                    value: z.string().optional().nullable(),
                  })
                  .describe('Deprecated.'),
              }),
              z.object({
                resource: z.never().optional(),
                site: z.never().optional(),
                locations: z.never().optional(),
                conditions: z
                  .object({
                    conditions: z
                      .array(
                        z.object({
                          conditionType: z
                            .enum(['UNKNOWN_CONDITION_TYPE', 'FOLDER'])
                            .describe('Condition type.')
                            .optional(),
                          _id: z.string().describe('Condition ID.').optional(),
                          value: z
                            .string()
                            .describe(
                              'Expected value of the condition. When `conditionType` = "FOLDER", this is the folder path.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe(
                    "List of conditions restricting the user's access. Currently only folder conditions are supported."
                  ),
              }),
              z.object({
                resource: z.never().optional(),
                conditions: z.never().optional(),
                locations: z.never().optional(),
                site: z
                  .object({
                    _id: z.string().describe('Site ID.').optional(),
                    value: z
                      .string()
                      .describe('Site name.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Site where the assignment restrictions apply.'),
              }),
              z.object({
                resource: z.never().optional(),
                conditions: z.never().optional(),
                site: z.never().optional(),
                locations: z
                  .object({ ids: z.array(z.string()).max(20).optional() })
                  .describe(
                    'Business locations where the assignment restrictions apply. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).'
                  ),
              }),
            ])
          )
          .describe(
            'The asset where a user is assigned access in an assignment. When empty, the role covers all assets, with no restrictions to specific sites or folders.'
          )
          .optional(),
        subject: z
          .object({
            _id: z
              .string()
              .describe('Identity ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            subjectType: z
              .enum([
                'UNKNOWN',
                'ACCOUNT',
                'USER',
                'USER_GROUP',
                'MEMBER_GROUP',
                'VISITOR_GROUP',
                'EXTERNAL_APP',
                'ACCOUNT_GROUP',
                'WIX_APP',
              ])
              .describe('Identity type.')
              .optional(),
          })
          .describe(
            'Identity assigned to the asset in an assignment, referred to as subject. Supported subjects include user IDs, account IDs, and app IDs.'
          )
          .optional(),
      })
    )
    .optional(),
});
